/**
 * Safe platform detection.
 *
 * Every helper here must work in a plain browser (and in jsdom during tests)
 * where Capacitor is not present. Nothing in this module imports a Capacitor
 * plugin at module scope, so the web bundle keeps working unchanged.
 */

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
};

const getCapacitor = (): CapacitorGlobal | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
};

/** True only inside the Android/iOS Capacitor webview. */
export const isNativeApp = (): boolean => {
  try {
    return getCapacitor()?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

/** 'web' | 'ios' | 'android' — always 'web' outside the native shell. */
export const getPlatform = (): 'web' | 'ios' | 'android' => {
  try {
    const platform = getCapacitor()?.getPlatform?.();
    return platform === 'ios' || platform === 'android' ? platform : 'web';
  } catch {
    return 'web';
  }
};

export const isIOS = (): boolean => getPlatform() === 'ios';
export const isAndroid = (): boolean => getPlatform() === 'android';

/** True when a specific Capacitor plugin can actually be called. */
export const isPluginAvailable = (name: string): boolean => {
  try {
    const capacitor = getCapacitor();
    if (!capacitor?.isNativePlatform?.()) return false;
    return capacitor.isPluginAvailable?.(name) === true;
  } catch {
    return false;
  }
};

/**
 * Run a native-only routine, swallowing any failure so a missing plugin can
 * never break the web app or a native cold start.
 */
export const runNative = async (task: () => Promise<void> | void): Promise<void> => {
  if (!isNativeApp()) return;
  try {
    await task();
  } catch (error) {
    console.warn('[native] task skipped', error);
  }
};
